import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Landmark, Search, Loader2, ExternalLink, Sparkles, PlayCircle, RefreshCw, TrendingUp, TrendingDown, Scale, FileText, Copy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { pollJob } from '../../utils/aiJobs';
import { GLOBAL_COUNTRIES as COUNTRIES } from '../family/shared';
import { BillProgress } from './BillProgress';

const HAS_BILLS = ['chile', 'argentina', 'colombia', 'mexico', 'peru', 'ecuador', 'brasil', 'espana', 'eeuu'];

const IMPACT = {
  favorece: { cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300', icon: TrendingUp, label: 'Favorece' },
  perjudica: { cls: 'border-red-500/40 bg-red-500/10 text-red-300', icon: TrendingDown, label: 'Perjudica' },
  mixto: { cls: 'border-amber-500/40 bg-amber-500/10 text-amber-300', icon: Scale, label: 'Mixto' },
};

const AlertCard = ({ a }) => {
  const { api } = useAuth();
  const [arg, setArg] = useState(null);
  const [busy, setBusy] = useState(false);
  const s = IMPACT[a.impacto] || IMPACT.mixto;
  const useInBrief = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/legislative/alerts/${a.id}/argument`);
      setArg(data);
      toast.success('Argumento guardado en el caso: se incluirá en el próximo escrito que generes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo redactar el argumento');
    } finally {
      setBusy(false);
    }
  };
  const Icon = s.icon;
  return (
    <div data-testid={`leg-alert-${a.id}`} className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${s.cls}`}><Icon className="h-3 w-3" /> {s.label}</span>
        <span className="text-xs text-slate-400">Relevancia {a.relevancia} · aprobación {a.probabilidad_aprobacion || '—'}</span>
      </div>
      <p className="text-sm text-white"><span className="font-semibold">Boletín {a.boletin}:</span> {a.bill_title}</p>
      <p className="text-xs text-amber-300">Tu caso: {a.matter_title}</p>
      <p className="text-sm text-slate-300">{a.explicacion}</p>
      <p className="rounded-lg bg-indigo-500/10 p-2 text-sm text-indigo-100"><span className="font-semibold">Cómo sacarle provecho:</span> {a.como_aprovechar}</p>
      <div className="flex flex-wrap items-center gap-3">
        {a.link && <a href={a.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-300 hover:underline">{a.source_type === 'sesion' ? 'Ver sesión oficial' : 'Ver tramitación oficial'} <ExternalLink className="h-3 w-3" /></a>}
        {a.impacto !== 'perjudica' && (
          <button data-testid={`leg-use-brief-${a.id}`} onClick={useInBrief} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 px-3 py-1 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />} {a.argument_id ? 'Ver argumento guardado' : 'Usar en escrito'}
          </button>
        )}
      </div>
      {arg && (
        <div data-testid={`leg-argument-${a.id}`} className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-slate-200">
          <div className="mb-1 flex items-center justify-between gap-2"><p className="font-semibold text-emerald-200">{arg.titulo}</p>
            <button onClick={() => { navigator.clipboard.writeText(arg.parrafo); toast.success('Copiado'); }} className="text-slate-400 hover:text-white" title="Copiar"><Copy className="h-4 w-4" /></button></div>
          <p className="whitespace-pre-line">{arg.parrafo}</p>
        </div>
      )}
    </div>
  );
};

export const CongressWatch = () => {
  const { api, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [bills, setBills] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState('');
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(null);
  const [limit, setLimit] = useState(60);
  const [country, setCountry] = useState('chile');
  const [follows, setFollows] = useState(new Set());
  useEffect(() => { api.get('/legislative/follows').then((r) => setFollows(new Set(r.data.follows.map((f) => `${f.country}|${f.boletin}`)))).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setFollow = (boletin, on) => setFollows((s) => { const n = new Set(s); const k = `${country}|${boletin}`; if (on) n.add(k); else n.delete(k); return n; });

  const reqRef = React.useRef(0);
  const loadBills = (query = q, lim = limit, c = country) => {
    const id = ++reqRef.current;
    const apply = (rows, last) => { if (id === reqRef.current) { setBills(rows); if (last !== undefined) setLastRun(last); } };
    return HAS_BILLS.includes(c) ? api.get(`/legislative/${c}/bills`, { params: { q: query || undefined, limit: lim } }).then((r) => apply(r.data.bills, r.data.last_run)).catch(() => apply([])) : Promise.resolve(apply([]));
  };
  const loadAlerts = () => api.get('/legislative/alerts').then((r) => setAlerts(r.data.alerts)).catch(() => {});

  useEffect(() => {
    loadAlerts();
    api.get('/legislative/prefs').then((r) => setEmail(r.data.legislative_email)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get(`/legislative/${country}/videos`).then((r) => setVideos(r.data.videos)).catch(() => setVideos([]));
    setQ(''); setLimit(60); setBills([]); loadBills('', 60, country);
  }, [api, country]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = alerts.filter((a) => (a.country || 'chile') === country);

  const runJob = async (kind, url, msg) => {
    setBusy(kind);
    try {
      const { data } = await api.post(url);
      toast.info(msg);
      const res = await pollJob(api, data.job_id);
      await Promise.all([loadAlerts(), loadBills()]);
      toast.success(kind === 'analyze' ? `Análisis listo: ${res.alerts} impacto(s) nuevo(s) en tus casos` : 'Proyectos actualizados');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'No se pudo completar');
    } finally {
      setBusy(null);
    }
  };

  const toggleEmail = async () => {
    try {
      const { data } = await api.put('/legislative/prefs', { legislative_email: !email });
      setEmail(data.legislative_email);
    } catch { toast.error('No se pudo guardar la preferencia'); }
  };

  return (
    <div className="space-y-8" data-testid="congress-watch">
      <div className="flex flex-wrap gap-2">
        {COUNTRIES.map((c) => (
          <button key={c.code} data-testid={`leg-country-${c.code}`} onClick={() => { setBills([]); setCountry(c.code); }}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${country === c.code ? 'border-indigo-400 bg-indigo-600 text-white' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
            {c.flag} {c.name}
          </button>
        ))}
      </div>
      <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-white font-['Outfit']"><Sparkles className="h-5 w-5 text-indigo-300" /> Impacto en tus casos</h2>
            <p className="text-sm text-slate-400">Cerebro cruza cada día los proyectos de ley con tus casos del CRM y te dice si te favorecen o perjudican, y cómo sacarles provecho.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input data-testid="leg-email-toggle" type="checkbox" checked={email} onChange={toggleEmail} /> Avisarme también por correo</label>
            <button data-testid="leg-analyze-btn" onClick={() => runJob('analyze', '/legislative/analyze-mine', 'Cerebro está analizando tus casos…')} disabled={!!busy}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-400 disabled:opacity-60">
              {busy === 'analyze' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analizar mis casos ahora
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {shown.length === 0 && <p data-testid="leg-alerts-empty" className="text-sm text-slate-500">Aún no hay actividad legislativa que afecte tus casos de {COUNTRIES.find((c) => c.code === country)?.name}.</p>}
          {shown.map((a) => <AlertCard key={a.id} a={a} />)}
        </div>
      </section>

      {!HAS_BILLS.includes(country) && (
        <p data-testid="leg-latam-note" className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-sm text-slate-400">{videos.length > 0 ? 'En este país Cerebro analiza las sesiones oficiales del Congreso o Asamblea (videos y descripciones de sus canales oficiales) para detectar reformas que afecten tus casos.' : 'Por ahora no hay una fuente oficial legible de proyectos ni de sesiones para este país, por lo que no se generan alertas automáticas.'}</p>
      )}
      {HAS_BILLS.includes(country) && <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-white font-['Outfit']"><Landmark className="h-5 w-5 text-amber-400" /> Proyectos de ley en tramitación</h2>
          <div className="flex items-center gap-2">
            {lastRun && <span className="text-xs text-slate-500">Actualizado {new Date(lastRun.finished_at).toLocaleString('es-CL')}</span>}
            {user?.is_admin && (
              <button data-testid="leg-refresh-btn" onClick={() => runJob('refresh', '/legislative/refresh', 'Consultando Senado y Cámara…')} disabled={!!busy} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800">
                {busy === 'refresh' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Actualizar
              </button>
            )}
          </div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); loadBills(); }} className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="leg-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por tema o boletín: notarios, arriendo, pensión, 18078…"
            className="w-full rounded-xl border border-slate-700 bg-slate-950/60 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-400 focus:outline-none" />
        </form>
        <div className="grid gap-2" data-testid="leg-bills-list">
          {bills.length === 0 && <p className="text-sm text-slate-500">Sin proyectos para mostrar.</p>}
          {bills.map((b) => (
            <a key={b.boletin} href={b.link} target="_blank" rel="noreferrer" data-testid={`leg-bill-${b.boletin}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 hover:border-amber-500/40 transition-colors">
              <p className="text-sm text-white"><span className="font-semibold text-amber-300">{b.boletin}</span> · {b.titulo}</p>
              <p className="mt-1 text-xs text-slate-400">{b.etapa}{b.subetapa ? ` · ${b.subetapa}` : ''} · {b.camara_origen} · {b.iniciativa}{b.urgencia && b.urgencia !== 'Sin urgencia' ? ` · Urgencia ${b.urgencia}` : ''}{b.ley_numero ? ` · Ley N° ${b.ley_numero} (D.O. ${b.diario_oficial})` : ''}</p>
              {b.ultimo_tramite?.descripcion && <p className="mt-1 text-xs text-slate-500">Último trámite {b.ultimo_tramite.fecha}: {b.ultimo_tramite.descripcion}</p>}
              {['peru', 'ecuador'].includes(country) && <BillProgress country={country} boletin={b.boletin} followed={follows.has(`${country}|${b.boletin}`)} onFollow={setFollow} />}
            </a>
          ))}
        </div>
        {bills.length >= limit && limit < 200 && (
          <button data-testid="leg-load-more" onClick={() => { const l = limit + 60; setLimit(l); loadBills(q, l); }} className="mt-3 rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-300 hover:bg-slate-800">Ver más proyectos</button>
        )}
      </section>}

      <section>
        <h2 className="mb-3 flex items-center gap-2 text-base md:text-lg font-semibold text-white font-['Outfit']"><PlayCircle className="h-5 w-5 text-red-400" /> Sesiones y videos oficiales</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="leg-videos">
          {videos.length === 0 && <p data-testid="leg-videos-empty" className="text-sm text-slate-500 sm:col-span-4">El canal oficial de este país no publica videos recientes en un formato que podamos leer. Revisa su portal oficial.</p>}
          {videos.map((v) => (
            <a key={v.video_id} href={v.url} target="_blank" rel="noreferrer" data-testid={`leg-video-${v.video_id}`} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/60 hover:border-red-500/40 transition-colors">
              <img src={`https://i.ytimg.com/vi/${v.video_id}/mqdefault.jpg`} alt={v.title} className="aspect-video w-full object-cover" loading="lazy" />
              <div className="p-2"><p className="line-clamp-2 text-xs text-white">{v.title}</p><p className="text-[11px] text-slate-500">{v.channel} · {new Date(v.published).toLocaleDateString('es-CL')}</p></div>
            </a>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">{country === 'chile' ? 'Fuentes oficiales: tramitacion.senado.cl, opendata.camara.cl y canales de YouTube de la Cámara y el Senado. Las leyes publicadas muestran su número y fecha en el Diario Oficial.' : country === 'argentina' ? 'Fuentes oficiales: datos abiertos de la Cámara de Diputados de la Nación (datos.hcdn.gob.ar) y canal del Senado.' : country === 'colombia' ? 'Fuentes oficiales: Cámara de Representantes en datos.gov.co y canales del Congreso.' : 'Fuente: canales oficiales de YouTube del Congreso o Asamblea del país.'}</p>
      </section>
    </div>
  );
};
